import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Calendar } from '../../../components/ui/calendar';
import { Loader2, Plus, Search, Check, UserCheck, X, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { blockNonNumericKeys, sanitizeNumericPaste } from '../../../utils/numericValidation';
import { formatDateUS } from './dateUtils';
import { cn } from '@/lib/utils';
import { LocationSelect } from './LocationSelect';

/**
 * Reusable create/edit modal for Division 26 modules.
 *
 * Props:
 *  - open, onOpenChange
 *  - config: { moduleName, fields: [...fieldDef] }
 *  - editItem: null | item object
 *  - onSave: (formData) => Promise<void>
 *  - manufacturers: [] (for manufacturer field type)
 *  - onCreateManufacturer: (name) => Promise<manufacturer>
 */
export const Division26CreateModal = ({
  open,
  onOpenChange,
  config,
  editItem,
  onSave,
  manufacturers = [],
  onCreateManufacturer,
  extraSection = null,
  projectId,
}) => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [mfrSearch, setMfrSearch] = useState('');
  const [creatingMfr, setCreatingMfr] = useState(false);
  // Exclusive dropdown behavior — only one custom select/popover open at a time
  const [openDropdown, setOpenDropdown] = useState(null);
  const setDropdown = (key, isOpen) => setOpenDropdown(prev => isOpen ? key : (prev === key ? null : prev));
  const selectProps = (key) => ({ open: openDropdown === key, onOpenChange: (o) => setDropdown(key, o), modal: false });
  const popoverProps = (key) => ({ open: openDropdown === key, onOpenChange: (o) => setDropdown(key, o) });

  // Init form from config defaults or editItem
  useEffect(() => {
    if (!open) return;
    const initial = {};
    config.fields.forEach(f => {
      initial[f.key] = editItem ? (editItem[f.key] ?? f.defaultValue ?? '') : (f.defaultValue ?? '');
    });
    // Carry over locationId from editItem if available
    if (editItem?.locationId) initial.locationId = editItem.locationId;
    setForm(initial);
    setErrors({});
    setMfrSearch('');
    setOpenDropdown(null);
  }, [open, editItem, config.fields]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const errs = {};
    config.fields.forEach(f => {
      const val = form[f.key];
      if (f.required && (val === '' || val === null || val === undefined)) {
        errs[f.key] = `${f.label} is required`;
      }
      if (f.numeric && val !== '' && val !== null && val !== undefined) {
        const num = parseFloat(val);
        if (isNaN(num) || num < 0) {
          errs[f.key] = `${f.label} must be a non-negative number`;
        }
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form, editItem);
      onOpenChange(false);
    } catch (err) {
      const data = err.response?.data;
      // Handle 409 Conflict (unique field violation) with field-level error
      if (err.response?.status === 409 && data?.detail?.field) {
        setErrors(prev => ({ ...prev, [data.detail.field]: data.detail.message }));
      } else {
        const msg = typeof data?.detail === 'string' ? data.detail : data?.detail?.message;
        toast.error(msg || `Failed to save ${config.moduleName.toLowerCase()}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const filteredMfrs = manufacturers.filter(m => m.name.toLowerCase().includes(mfrSearch.toLowerCase()));
  const showAddNew = mfrSearch.trim().length > 0 && !manufacturers.some(m => m.name.toLowerCase() === mfrSearch.trim().toLowerCase());

  const handleCreateMfr = async (fieldKey) => {
    if (creatingMfr) return;
    setCreatingMfr(true);
    try {
      const newMfr = await onCreateManufacturer(mfrSearch.trim());
      handleChange(fieldKey, newMfr.id);
      setMfrSearch('');
      setOpenDropdown(null);
      toast.success(`"${newMfr.name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create option');
    } finally {
      setCreatingMfr(false);
    }
  };

  const renderField = (field) => {
    const fieldError = errors[field.key];

    if (field.type === 'location') {
      return (
        <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <LocationSelect
            value={form.locationId || ''}
            displayValue={form[field.key] || ''}
            onChange={(id, name) => { handleChange(field.key, name); setForm(prev => ({ ...prev, locationId: id })); }}
            projectId={projectId}
            error={fieldError}
            placeholder={field.placeholder || 'Select location...'}
            {...popoverProps(`location-${field.key}`)}
          />
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    if (field.type === 'manufacturer') {
      const selectedItem = manufacturers.find(m => m.id === form[field.key]);
      return (
        <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <Popover {...popoverProps(`mfr-${field.key}`)}>
            <PopoverTrigger asChild>
              <div
                className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${fieldError ? 'border-red-500' : 'border-input'}`}
                data-testid={`searchable-select-${field.key}`}
              >
                {selectedItem ? (
                  <div className="flex items-center justify-between w-full">
                    <span className="text-sm">{selectedItem.name}</span>
                    <X className="h-4 w-4 text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); handleChange(field.key, ''); }} />
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">Select {field.label.toLowerCase()}...</span>
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-h-60 overflow-y-auto" align="start" sideOffset={4}>
              <div className="sticky top-0 bg-white p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input className="pl-8" placeholder="Search or add new..." value={mfrSearch} onChange={(e) => setMfrSearch(e.target.value)} autoFocus data-testid={`searchable-input-${field.key}`} />
                </div>
              </div>
              <div>
                {filteredMfrs.map((m) => (
                  <div
                    key={m.id}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${m.id === form[field.key] ? 'bg-slate-50 font-medium' : ''}`}
                    onClick={() => { handleChange(field.key, m.id); setMfrSearch(''); setOpenDropdown(null); }}
                    data-testid={`option-${field.key}-${m.id}`}
                  >
                    {m.id === form[field.key] && <Check className="h-4 w-4 mr-2 text-orange-500" />}
                    <span className={m.id === form[field.key] ? '' : 'ml-6'}>{m.name}</span>
                    {m.isMine && <UserCheck className="h-3.5 w-3.5 ml-auto text-orange-400 shrink-0" title="Added by you" />}
                  </div>
                ))}
                {showAddNew && (
                  <div className="flex items-center px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-orange-600 font-medium border-t" onClick={() => handleCreateMfr(field.key)} data-testid={`add-new-${field.key}-btn`}>
                    {creatingMfr ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Add new: &quot;{mfrSearch.trim()}&quot;
                  </div>
                )}
                {filteredMfrs.length === 0 && !showAddNew && <div className="px-3 py-2 text-sm text-slate-400">No results</div>}
              </div>
            </PopoverContent>
          </Popover>
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    if (field.type === 'rating') {
      return (
        <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={form[field.key] ?? ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              onKeyDown={blockNonNumericKeys}
              min={0}
              placeholder="Value"
              className={`flex-1 ${fieldError ? 'border-red-500' : ''}`}
              data-testid={`input-${field.key}`}
            />
            <Select value={form[field.unitKey] || ''} onValueChange={(val) => handleChange(field.unitKey, val)} {...selectProps(`rating-${field.unitKey}`)}>
              <SelectTrigger className="w-24" data-testid={`select-${field.unitKey}`}>
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectContent>
                {field.unitOptions.map(u => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <Select value={form[field.key] || ''} onValueChange={(val) => handleChange(field.key, val)} {...selectProps(`select-${field.key}`)}>
            <SelectTrigger className={fieldError ? 'border-red-500' : ''} data-testid={`select-${field.key}`}>
              <SelectValue placeholder={`Select ${field.label.toLowerCase()}...`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(opt => (
                <SelectItem key={opt.value ?? opt} value={opt.value ?? opt}>{opt.label ?? opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    if (field.type === 'grouped_select') {
      return (
        <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <Select value={form[field.key] || ''} onValueChange={(val) => handleChange(field.key, val)} {...selectProps(`grouped-${field.key}`)}>
            <SelectTrigger className={fieldError ? 'border-red-500' : ''} data-testid={`select-${field.key}`}>
              <SelectValue placeholder={`Select ${field.label.toLowerCase()}...`} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {Object.entries(field.groups).map(([groupLabel, groupItems]) => (
                <SelectGroup key={groupLabel}>
                  <SelectLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{groupLabel}</SelectLabel>
                  {groupItems.map(item => (
                    <SelectItem key={item} value={item}>{item}</SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    if (field.type === 'numeric_unit') {
      return (
        <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={form[field.key] ?? ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              onKeyDown={blockNonNumericKeys}
              onPaste={sanitizeNumericPaste}
              min={0}
              placeholder={field.placeholder || 'Value'}
              className={`flex-1 ${fieldError ? 'border-red-500' : ''}`}
              data-testid={`input-${field.key}`}
            />
            <span className="text-sm font-medium text-slate-500 shrink-0">{field.unitLabel}</span>
          </div>
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    if (field.type === 'numeric') {
      return (
        <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <Input
            type="number"
            value={form[field.key] ?? ''}
            onChange={(e) => handleChange(field.key, e.target.value)}
            onKeyDown={blockNonNumericKeys}
            onPaste={sanitizeNumericPaste}
            min={0}
            placeholder={field.placeholder || ''}
            className={fieldError ? 'border-red-500' : ''}
            data-testid={`input-${field.key}`}
          />
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    if (field.type === 'date') {
      const rawVal = form[field.key] || '';
      const dateObj = rawVal ? new Date(rawVal + 'T00:00:00') : undefined;
      return (
        <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <Popover {...popoverProps(`date-${field.key}`)}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal h-9",
                  !rawVal && "text-muted-foreground",
                  fieldError && "border-red-500"
                )}
                data-testid={`input-${field.key}`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {rawVal ? formatDateUS(rawVal) : `Select date...`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dateObj}
                onSelect={(date) => {
                  if (date) {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    handleChange(field.key, `${y}-${m}-${d}`);
                  } else {
                    handleChange(field.key, '');
                  }
                  setOpenDropdown(null);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.key} className={`space-y-1 ${field.fullWidth ? 'col-span-2' : ''}`} data-testid={`field-${field.key}`}>
          <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
          <Textarea
            value={form[field.key] || ''}
            onChange={(e) => handleChange(field.key, e.target.value)}
            placeholder={field.placeholder || ''}
            className={fieldError ? 'border-red-500' : ''}
            rows={3}
            data-testid={`textarea-${field.key}`}
          />
          {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
      );
    }

    // Default: text/number input
    return (
      <div key={field.key} className="space-y-1" data-testid={`field-${field.key}`}>
        <Label className="text-sm">{field.label}{field.required && ' *'}</Label>
        <Input
          type={field.numeric ? 'number' : 'text'}
          value={form[field.key] ?? ''}
          onChange={(e) => handleChange(field.key, field.numeric ? e.target.value : e.target.value)}
          onKeyDown={field.numeric ? blockNonNumericKeys : undefined}
          min={field.numeric ? 0 : undefined}
          placeholder={field.placeholder || ''}
          className={fieldError ? 'border-red-500' : ''}
          data-testid={`input-${field.key}`}
        />
        {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
      </div>
    );
  };

  // Separate regular fields from fullWidth ones
  const regularFields = config.fields.filter(f => !f.fullWidth);
  const fullWidthFields = config.fields.filter(f => f.fullWidth);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid={`${config.testIdPrefix}-modal`}>
        <DialogHeader>
          <DialogTitle>{editItem ? `Edit ${config.moduleName}` : `Create ${config.moduleName}`}</DialogTitle>
          <DialogDescription>
            {editItem ? `Update the ${config.moduleName.toLowerCase()} details below.` : `Fill in the details to create a new ${config.moduleName.toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            {regularFields.map(renderField)}
          </div>
          {fullWidthFields.length > 0 && (
            <div className="mt-4 space-y-4">
              {fullWidthFields.map(renderField)}
            </div>
          )}
          {extraSection}
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t shrink-0">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid={`${config.testIdPrefix}-cancel-btn`}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid={`${config.testIdPrefix}-save-btn`}>
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {editItem ? 'Update' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
